import { create } from 'zustand';

// ============================================================================
// Navigation Store
// Tracks route changes and current step in the user journey (runtime only)
// ============================================================================

export type NavigationStep = 'home' | 'browse' | 'configure' | 'checkout' | 'other';

interface NavigationState {
  currentPath: string;
  previousPath: string | null;
  history: string[];
  currentStep: NavigationStep;
}

interface NavigationActions {
  onRouteChange: (path: string) => void;
  setStep: (step: NavigationStep) => void;
  resetHistory: () => void;
}

type NavigationStore = NavigationState & NavigationActions;

/**
 * Maps a pathname to a navigation step
 */
const getStepFromPath = (path: string): NavigationStep => {
  if (path === '/') return 'home';
  if (path.startsWith('/menu')) return 'browse';
  if (path.startsWith('/pizza-details') || path.startsWith('/product')) return 'configure';
  if (path.startsWith('/basket') || path.startsWith('/checkout')) return 'checkout';
  return 'other';
};

const initialState: NavigationState = {
  currentPath: '/',
  previousPath: null,
  history: [],
  currentStep: 'home',
};

export const useNavigationStore = create<NavigationStore>()((set) => ({
  ...initialState,

  onRouteChange: (path) => {
    set((state) => ({
      previousPath: state.currentPath,
      currentPath: path,
      history: [...state.history, path].slice(-20), // Keep last 20 entries
      currentStep: getStepFromPath(path),
    }));
  },

  setStep: (step) => {
    set({ currentStep: step });
  },

  resetHistory: () => {
    set(initialState);
  },
}));

// Selector hooks for optimized re-renders
export const selectCurrentPath = (state: NavigationStore) => state.currentPath;
export const selectPreviousPath = (state: NavigationStore) => state.previousPath;
export const selectNavigationHistory = (state: NavigationStore) => state.history;
export const selectCurrentStep = (state: NavigationStore) => state.currentStep;
